import type { MapCoordsGranularity } from '@revelio/data-access';

import { LocationType } from '../types';
import { BoundingBoxes } from './bounding-boxes';

const COUNTRIES = new Set([
  ...Object.keys(BoundingBoxes[LocationType.COUNTRY]).map((c) =>
    c.toLowerCase()
  ),
]);

const REGIONS = new Set([
  ...Object.keys(BoundingBoxes[LocationType.REGION]).map((r) =>
    r.toLowerCase()
  ),
]);

function isNonMetropolitanAreaLabel(label: string | undefined | null): boolean {
  if (!label) return false;
  const l = label.toLowerCase().trim();
  return (
    l.includes('nonmetropolitan area') ||
    l.includes('non-metropolitan area') ||
    l.endsWith('non-metro') // transitionsV2
  );
}

export function isMetropolitanAreaLabel(
  label: string | undefined | null
): boolean {
  if (!label) return false;
  const l = label.toLowerCase().trim();
  if (isNonMetropolitanAreaLabel(label)) return false;
  return l.includes('metropolitan area') || l.endsWith('metro area');
}

export function isCountry(name: string): boolean {
  const normalizedName = name.toLowerCase().trim();

  const baseName = normalizedName
    .replace(/non-metro$/i, '')
    .replace(/nonmetro$/i, '')
    .trim();

  return COUNTRIES.has(baseName);
}

export function isRegion(name: string): boolean {
  const normalizedName = name.toLowerCase().trim();

  const baseName = normalizedName
    .replace(/non-metro$/i, '')
    .replace(/nonmetro$/i, '')
    .trim();

  return REGIONS.has(baseName);
}

// Determine location type for mapping entities to map layers
// Caution: this should not be used to determine location type for the coordinate-resolver (GeographySummaryMapData),
// as LocationType and MapCoordsGranularity differ ("Canada Non-Metro" => LocationType.COUNTRY_NON_METRO, but MapCoordsGranularity.Msa).
export function determineLocationType(
  label: string,
  granularity: MapCoordsGranularity | string,
  name?: string
): LocationType {
  if (name?.includes(' metropolitan area')) {
    return LocationType.METRO_AREA;
  }

  if (granularity) {
    switch (granularity) {
      case 'region':
      case 'REGION':
        return LocationType.REGION;
      case 'country':
      case 'COUNTRY':
        return LocationType.COUNTRY;
      case 'msa':
      case 'MSA':
        return LocationType.METRO_AREA;
    }
  }

  return LocationType.METRO_AREA;
}
